import { lazy } from "react";
import useProtectedRoutes from "../hooks/useProtectedRoutes";
import SidebarLinks from "../components/SidebarLinks.js";

// project imports
import MainLayout from "../layout/MainLayout";
import Loadable from "./../components/Loadable";

// dashboard routing
const Home = Loadable(lazy(() => import("../views/home")));
const Employee = Loadable(lazy(() => import("../views/employee")));
const EmployeeManage = Loadable(lazy(() => import("../views/employee/form")));
const Departments = Loadable(lazy(() => import("../views/departments")));
const Announcement = Loadable(lazy(() => import("../views/announcement")));
const Holiday = Loadable(lazy(() => import("../views/holiday")));
const Flexibility = Loadable(lazy(() => import("../views/flexibility")));
const Leave = Loadable(lazy(() => import("../views/leave")));
const Profile = Loadable(lazy(() => import("../views/profile")));
const MyAtendance = Loadable(lazy(() => import("../views/my_attendance")));
const ManageAttendance = Loadable(
  lazy(() => import("../views/manage_attendance")),
);
const SuspensionOvertime = Loadable(
  lazy(() => import("../views/suspension-overtime")),
);
const Notifications = Loadable(lazy(() => import("../views/notifications")));
const Reports = Loadable(lazy(() => import("../views/reports")));
const Statistics = Loadable(lazy(() => import("../views/statistics")));
const Shifts = Loadable(lazy(() => import("../views/shifts")));
const FinalAccounts = Loadable(lazy(() => import("../views/final_accounts")));
const CompanyTree = Loadable(lazy(() => import("../views/company-tree")));
const BonusesCuts = Loadable(lazy(() => import("../views/bonuses_cuts")));
const Overtime = Loadable(lazy(() => import("../views/overtime")));
const AdvancePayments = Loadable(
  lazy(() => import("../views/advance_payments")),
);

const Salary = Loadable(lazy(() => import("../views/salary")));
const ManageSalary = Loadable(lazy(() => import("../views/manage_salary")));
const Settings = Loadable(lazy(() => import("../views/settings")));
const EmployeeRatings = Loadable(
  lazy(() => import("../views/employee_ratings")),
);

const TeamLeader = Loadable(lazy(() => import("../views/teamleader")));
const RemotelyExtra = Loadable(lazy(() => import("../views/remotely_extra")));

const ProtectedRoute = ({ element, sidebarID }) => {
  const page_roles = SidebarLinks.find((item) => item.id === sidebarID)?.roles;
  const page_has_team_required = SidebarLinks.find(
    (item) => item.id === sidebarID,
  )?.has_team_required;

  const user = useProtectedRoutes();

  if (
    (page_roles && page_roles?.includes(user.role)) ||
    (user.has_team && page_has_team_required)
  ) {
    return element;
  } else {
    return null;
  }
};

// ==============================|| MAIN ROUTING ||============================== //

const MainRoutes = {
  path: "/",
  element: <MainLayout />,
  children: [
    {
      path: "/",
      element: <Home />,
    },
    {
      path: "/company-tree",
      children: [
        {
          path: "/company-tree",
          element: <ProtectedRoute element={<CompanyTree />} sidebarID={20} />,
        },
      ],
    },
    {
      path: "/statistics",
      children: [
        {
          path: "/statistics",
          element: <ProtectedRoute element={<Statistics />} sidebarID={2} />,
        },
      ],
    },
    {
      path: "/remotely-extra",
      children: [
        {
          path: "/remotely-extra",
          element: <ProtectedRoute element={<RemotelyExtra />} sidebarID={21} />,
        },
      ],
    },
    {
      path: "/team-leader",
      children: [
        {
          path: "/team-leader",
          element: <ProtectedRoute element={<TeamLeader />} sidebarID={62} />,
        },
      ],
    },
    {
      path: "/employee-ratings",
      children: [
        {
          path: "/employee-ratings",
          element: (
            <ProtectedRoute element={<EmployeeRatings />} sidebarID={19} />
          ),
        },
      ],
    },
    {
      path: "/employee",
      children: [
        {
          path: "/employee",
          element: <ProtectedRoute element={<Employee />} sidebarID={3} />,
        },
        {
          path: "/employee/add",
          element: (
            <ProtectedRoute element={<EmployeeManage />} sidebarID={3} />
          ),
        },
        {
          path: "/employee/edit/:id",
          element: (
            <ProtectedRoute element={<EmployeeManage />} sidebarID={3} />
          ),
        },
      ],
    },
    {
      path: "/departments",
      children: [
        {
          path: "/departments",
          element: <ProtectedRoute element={<Departments />} sidebarID={7} />,
        },
      ],
    },
    {
      path: "/announcements",
      children: [
        {
          path: "/announcements",
          element: <ProtectedRoute element={<Announcement />} sidebarID={8} />,
        },
      ],
    },
    {
      path: "/holidays",
      children: [
        {
          path: "/holidays",
          element: <ProtectedRoute element={<Holiday />} sidebarID={9} />,
        },
      ],
    },
    {
      path: "/flexibility",
      children: [
        {
          path: "/flexibility",
          element: <ProtectedRoute element={<Flexibility />} sidebarID={10} />,
        },
      ],
    },
    {
      path: "/leave",
      children: [
        {
          path: "/leave",
          element: <Leave />,
        },
      ],
    },
    {
      path: "/profile",
      children: [
        {
          path: "/profile",
          element: <Profile />,
        },
      ],
    },
    {
      path: "/my-attendance",
      children: [
        {
          path: "/my-attendance",
          element: <MyAtendance />,
        },
      ],
    },
    {
      path: "/attendance",
      children: [
        {
          path: "/attendance",
          element: (
            <ProtectedRoute element={<ManageAttendance />} sidebarID={6} />
          ),
        },
      ],
    },
    {
      path: "/suspension-overtime",
      children: [
        {
          path: "/suspension-overtime",
          element: (
            <ProtectedRoute element={<SuspensionOvertime />} sidebarID={678} />
          ),
        },
      ],
    },
    {
      path: "/reports",
      children: [
        {
          path: "/reports",
          element: <ProtectedRoute element={<Reports />} sidebarID={13} />,
        },
      ],
    },
    {
      path: "/notifications",
      children: [
        {
          path: "/notifications",
          element: <Notifications />,
        },
      ],
    },

    {
      path: "/shifts",
      children: [
        {
          path: "/shifts",
          element: <ProtectedRoute element={<Shifts />} sidebarID={4} />,
        },
      ],
    },
    {
      path: "/final-accounts",
      children: [
        {
          index: true,
          element: <FinalAccounts />,
        },
      ],
    },
    {
      path: "/bonuses-cuts",
      children: [
        {
          path: "/bonuses-cuts",
          element: <ProtectedRoute element={<BonusesCuts />} sidebarID={14} />,
        },
      ],
    },
    {
      path: "/overtime",
      children: [
        {
          path: "/overtime",
          element: <ProtectedRoute element={<Overtime />} sidebarID={140} />,
        },
      ],
    },
    {
      path: "/advance-payment",
      children: [
        {
          path: "/advance-payment",
          element: (
            <ProtectedRoute element={<AdvancePayments />} sidebarID={15} />
          ),
        },
      ],
    },
    {
      path: "/salary",
      children: [
        {
          path: "/salary",
          element: <ProtectedRoute element={<Salary />} sidebarID={16} />,
        },
      ],
    },
    {
      path: "/manage-salary",
      children: [
        {
          path: "/manage-salary",
          element: <ProtectedRoute element={<ManageSalary />} sidebarID={17} />,
        },
      ],
    },
    {
      path: "/settings",
      element: <ProtectedRoute element={<Settings />} sidebarID={18} />,
    },
  ],
};

export default MainRoutes;
